import React, { useEffect, useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import gsap from "gsap";
import $ from "jquery";
import ApiURl from "../controllers/Api";
import imgLocation from "../controllers/imagePath";

const FALLBACK_IMG =
  "data:image/svg+xml;utf8," +
  encodeURIComponent(`
  <svg xmlns="http://www.w3.org/2000/svg" width="300" height="300">
    <rect width="100%" height="100%" fill="#f2f2f2"/>
    <text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle"
      font-family="Arial" font-size="18" fill="#777">
      No Image
    </text>
  </svg>
`);

async function postPhp(url, payload) {
  try {
    return await $.ajax({
      url,
      method: "POST",
      contentType: "application/json",
      dataType: "json",
      data: JSON.stringify(payload),
    });
  } catch (jsonErr) {
    return await $.ajax({
      url,
      method: "POST",
      dataType: "json",
      data: payload,
    });
  }
}

function UnderPriceProducts() {
  const navigate = useNavigate();
  const pageRef = useRef(null);
  const headerRef = useRef(null);
  const productsGridRef = useRef(null);
  const cardsRef = useRef({});

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [priceThreshold, setPriceThreshold] = useState(1000);
  const [wishlistAdded, setWishlistAdded] = useState({});
  const [wishlistBusy, setWishlistBusy] = useState({});
  const [showPopup, setShowPopup] = useState(false);
  const [popupMessage, setPopupMessage] = useState("");
  const [sortBy, setSortBy] = useState("price-low");
  const [filteredProducts, setFilteredProducts] = useState([]);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchUnderPriceProducts();
    loadWishlist();
  }, []);

  useEffect(() => {
    const onWishlistChanged = (e) => {
      const productId = e?.detail?.productId != null ? String(e.detail.productId) : null;
      const inWishlist = !!e?.detail?.inWishlist;
      if (!productId) return;

      setWishlistAdded((prev) => {
        const next = { ...prev };
        if (inWishlist) next[productId] = true;
        else delete next[productId];
        return next;
      });
    };

    window.addEventListener("wishlist:changed", onWishlistChanged);
    return () => window.removeEventListener("wishlist:changed", onWishlistChanged);
  }, []);

  // GSAP animations
  useEffect(() => {
    if (!pageRef.current || loading) return;

    const ctx = gsap.context(() => {
      gsap.fromTo(
        headerRef.current,
        { y: 30, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.8, ease: "power3.out" }
      );
    }, pageRef);

    return () => ctx.revert();
  }, [loading]);

  // Animate cards on list change
  useEffect(() => {
    if (loading || filteredProducts.length === 0) return;
    
    const nodes = Object.values(cardsRef.current).filter(Boolean);
    if (!nodes.length) return;

    gsap.killTweensOf(nodes);
    gsap.fromTo(
      nodes,
      { opacity: 0, y: 18, scale: 0.98 },
      { opacity: 1, y: 0, scale: 1, duration: 0.55, ease: "power2.out", stagger: 0.06 }
    );
  }, [filteredProducts, loading]);

  // Apply sorting
  useEffect(() => {
    if (!products.length) return;

    let sorted = [...products];
    
    switch (sortBy) {
      case "price-low":
        sorted.sort((a, b) => (a.min_price || 0) - (b.min_price || 0));
        break;
      case "price-high":
        sorted.sort((a, b) => (b.min_price || 0) - (a.min_price || 0));
        break;
      case "name-asc":
        sorted.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
        break;
      case "name-desc":
        sorted.sort((a, b) => (b.name || "").localeCompare(a.name || ""));
        break;
      default:
        break;
    }
    
    setFilteredProducts(sorted);
  }, [products, sortBy]);

  const fetchUnderPriceProducts = async () => {
    setLoading(true);
    try {
      const response = await $.getJSON(`${ApiURl}/getProductsUnderPrice.php`);
      if (response?.status === 'success') {
        setProducts(response.all_products || []);
        setFilteredProducts(response.all_products || []);
        setPriceThreshold(response.price_threshold || 1000);
      }
    } catch (error) {
      console.error("Error fetching under price products:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadWishlist = async () => {
    const userId = localStorage.getItem("user_id");
    if (!userId) return;

    try {
      const res = await $.getJSON(`${ApiURl}/getWishlistItems.php?user_id=${userId}`);
      const list = res?.wishlist || [];

      const map = {};
      for (const item of list) {
        if (item?.product_id != null) map[String(item.product_id)] = true;
      }
      setWishlistAdded(map);
    } catch (err) {
      console.error("Error loading wishlist:", err);
    }
  };

  const showWishlistPopup = (message) => {
    setPopupMessage(message);
    setShowPopup(true);
  };

  const handleWishlistToggle = async (product) => {
    const userId = localStorage.getItem("user_id");
    if (!userId) {
      alert("Please log in to manage your wishlist.");
      return;
    }

    const productId = String(product?.product_id ?? "");
    if (!productId) return;

    if (wishlistBusy[productId]) return;
    setWishlistBusy((prev) => ({ ...prev, [productId]: true }));

    const currentlyWishlisted = !!wishlistAdded[productId];

    try {
      if (currentlyWishlisted) {
        await postPhp(`${ApiURl}/removeWishlistItem.php`, { user_id: userId, product_id: productId });

        setWishlistAdded((prev) => {
          const next = { ...prev };
          delete next[productId];
          return next;
        });

        window.dispatchEvent(
          new CustomEvent("wishlist:changed", { detail: { productId, inWishlist: false } })
        );

        showWishlistPopup("Item Removed from Wishlist");
      } else {
        await postPhp(`${ApiURl}/addWishlistItem.php`, { user_id: userId, product_id: productId });

        setWishlistAdded((prev) => ({ ...prev, [productId]: true }));

        window.dispatchEvent(
          new CustomEvent("wishlist:changed", { detail: { productId, inWishlist: true } })
        );

        showWishlistPopup("Item Added to Wishlist");
      }
    } catch (error) {
      console.error("Wishlist toggle failed:", error);
      alert("Wishlist update failed. Please try again.");
    } finally {
      setWishlistBusy((prev) => {
        const next = { ...prev };
        delete next[productId];
        return next;
      });
    }
  };

  const handleClosePopup = () => setShowPopup(false);

  const toImgUrl = (val) => {
    if (!val || typeof val !== "string") return null;
    const s = val.trim();
    if (!s) return null;
    if (s.startsWith("http://") || s.startsWith("https://")) return s;
    return `${imgLocation}/${s}`;
  };

  const getProductImages = (product) => {
    const raw = product?.img_path || product?.images?.[0]?.filename;
    if (!raw) return { primary: FALLBACK_IMG, secondary: null };

    if (typeof raw === "string" && (raw.startsWith("http://") || raw.startsWith("https://"))) {
      return { primary: raw, secondary: null };
    }

    // Handle images array from API
    if (product?.images && Array.isArray(product.images) && product.images.length > 0) {
      const primary = product.images[0]?.url || FALLBACK_IMG;
      const secondary = product.images[1]?.url || null;
      return { primary, secondary };
    }

    // Handle string formats
    if (typeof raw === "string") {
      const trimmed = raw.trim();

      try {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed)) {
          const primary = toImgUrl(parsed[0]) || FALLBACK_IMG;
          const secondary = toImgUrl(parsed[1]);
          return { primary, secondary: secondary && secondary !== primary ? secondary : null };
        }
      } catch (e) {
        // ignore JSON parse
      }

      // comma separated
      if (trimmed.includes(",")) {
        const parts = trimmed
          .split(",")
          .map((x) => x.trim())
          .filter(Boolean);
        const primary = toImgUrl(parts[0]) || FALLBACK_IMG;
        const secondary = toImgUrl(parts[1]);
        return { primary, secondary: secondary && secondary !== primary ? secondary : null };
      }

      // pipe separated
      if (trimmed.includes("|")) {
        const parts = trimmed
          .split("|")
          .map((x) => x.trim())
          .filter(Boolean);
        const primary = toImgUrl(parts[0]) || FALLBACK_IMG;
        const secondary = toImgUrl(parts[1]);
        return { primary, secondary: secondary && secondary !== primary ? secondary : null };
      }

      const primary = toImgUrl(trimmed) || FALLBACK_IMG;
      return { primary, secondary: null };
    }

    return { primary: FALLBACK_IMG, secondary: null };
  };

  const formatPrice = (p) => {
    const priceValue = p?.min_price || p?.price || 0;
    const n = Number(priceValue) || 0;
    return `₹${n.toLocaleString("en-IN")}`;
  };

  const handleProductClick = (productId) => {
    navigate(`/ProductOverview/${productId}`);
    window.scrollTo(0, 0);
  };

  const onCardEnter = (key) => {
    const el = cardsRef.current[key];
    if (!el) return;
    gsap.to(el, {
      y: -6,
      boxShadow: "0px 20px 30px -10px rgba(0, 0, 0, 0.15)",
      duration: 0.22,
      ease: "power2.out",
    });
  };

  const onCardLeave = (key) => {
    const el = cardsRef.current[key];
    if (!el) return;
    gsap.to(el, {
      y: 0,
      boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.05)",
      duration: 0.22,
      ease: "power2.out",
    });
  };

  return (
    <div ref={pageRef} className="min-h-screen bg-[var(--user-bg,#E8D9D5)] pt-4 font-['Poppins',sans-serif] sm:pt-6 md:pt-8 lg:pt-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header with Stats */}
        <div ref={headerRef} className="mb-6 sm:mb-8">
          <div>
            <h1 className="text-lg font-medium tracking-[0.12em] uppercase text-neutral-900">
              BUDGET FRIENDLY
            </h1>
          </div>
          
          <div className="mt-3 sm:mt-4">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900">
              Products Under <span className="text-[#b29063]">₹{priceThreshold}</span>
            </h2>
            
            <p className="mt-2 text-gray-600 max-w-2xl">
              Discover amazing products all under ₹{priceThreshold}. Perfect for budget-conscious shoppers who don't want to compromise on quality.
            </p>
          </div>

          {/* Sort and Filter Bar */}
          <div className="mt-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 py-3 border-y border-gray-200">
            <div className="text-sm text-gray-600">
              Showing <span className="font-semibold text-[#b29063]">{filteredProducts.length}</span> products under ₹{priceThreshold}
            </div>
            
            <div className="flex items-center gap-3">
              <label className="text-sm text-gray-600">Sort by:</label>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="border border-gray-200 rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-[#b29063] bg-white"
              >
                <option value="price-low">Price: Low to High</option>
                <option value="price-high">Price: High to Low</option>
                <option value="name-asc">Name: A to Z</option>
                <option value="name-desc">Name: Z to A</option>
              </select>
            </div>
          </div>
        </div>

        {/* Products Grid */}
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="text-gray-600">Loading products...</div>
          </div>
        ) : filteredProducts.length === 0 ? (
          <div className="text-center py-16">
            <div className="text-6xl mb-4">🏷️</div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No products found</h3>
            <p className="text-gray-600">Check back later for new deals under ₹{priceThreshold}</p>
          </div>
        ) : (
          <div ref={productsGridRef} className="py-6 sm:py-8">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 sm:gap-3 md:gap-4 lg:gap-6">
              {filteredProducts.map((product, index) => {
                const pid = String(product.product_id);
                const key = `product-${pid}-${index}`;
                const isWishlisted = !!wishlistAdded[pid];
                const isBusy = !!wishlistBusy[pid];
                const { primary, secondary } = getProductImages(product);

                return (
                  <div
                    key={key}
                    ref={(el) => (cardsRef.current[key] = el)}
                    onMouseEnter={() => onCardEnter(key)}
                    onMouseLeave={() => onCardLeave(key)}
                    className="group relative bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow duration-200 border border-gray-100 cursor-pointer"
                    onClick={() => handleProductClick(product.product_id)}
                  >
                    <div className="p-2 sm:p-3">
                      <div className="flex justify-between items-center mb-1 sm:mb-2">
                        <span className="text-xs sm:text-sm font-medium text-gray-900">
                          {formatPrice(product)}
                        </span>

                        <button
                          type="button"
                          disabled={isBusy}
                          className={`text-sm sm:text-base ${
                            isWishlisted ? "text-red-500" : "text-gray-400"
                          } hover:text-red-500 transition-colors disabled:opacity-50`}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleWishlistToggle(product);
                          }}
                          aria-label={isWishlisted ? "Remove from wishlist" : "Add to wishlist"}
                          title={isWishlisted ? "Remove from wishlist" : "Add to wishlist"}
                        >
                          ❤
                        </button>
                      </div>

                      <div className="block">
                        <div className="relative h-32 sm:h-36 md:h-40 lg:h-48 flex items-center justify-center bg-gray-50 rounded-md overflow-hidden">
                          <img
                            src={primary}
                            alt={product.name || "Product"}
                            className={`absolute inset-0 w-full h-full object-contain transition-opacity duration-200 ${
                              secondary ? "opacity-100 group-hover:opacity-0" : "opacity-100"
                            }`}
                            onError={(e) => {
                              e.currentTarget.onerror = null;
                              e.currentTarget.src = FALLBACK_IMG;
                            }}
                          />
                          
                          {secondary && (
                            <img
                              src={secondary}
                              alt={`${product.name || "Product"} - alternate`}
                              className="absolute inset-0 w-full h-full object-contain opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                              onError={(e) => {
                                e.currentTarget.onerror = null;
                                e.currentTarget.src = primary || FALLBACK_IMG;
                              }}
                            />
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="px-2 sm:px-3 pb-2 sm:pb-3">
                      <div className="flex justify-between items-center">
                        <div className="text-xs sm:text-sm font-medium text-gray-900 truncate pr-2">
                          {product.name}
                        </div>

                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleProductClick(product.product_id);
                          }}
                          className="text-gray-600 hover:text-gray-900 text-base sm:text-lg"
                          aria-label="View product"
                          title="View product"
                        >
                          🛒
                        </button>
                      </div>

                      <div className="mt-1 sm:mt-2">
                        <span className="inline-block bg-gray-100 text-gray-700 text-xs px-2 py-1 rounded">
                          Under ₹{priceThreshold}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Wishlist Popup */}
      {showPopup && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          role="dialog"
          aria-modal="true"
        >
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-sm w-full transform animate-popIn">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">{popupMessage}</h2>
            <button
              onClick={handleClosePopup}
              className="w-full py-2 px-4 bg-[#b29063] text-white rounded-md hover:bg-[#9e7e53] transition"
            >
              Close
            </button>
          </div>
        </div>
      )}

      <style>{`
        @keyframes popIn {
          0% { transform: scale(0.9); opacity: 0; }
          100% { transform: scale(1); opacity: 1; }
        }
        .animate-popIn {
          animation: popIn 0.2s ease-out;
        }
      `}</style>
    </div>
  );
}

export default UnderPriceProducts;
